import { subscribeToParentCmp } from './parentCmp';

const GOOGLE_KEYS = ['analytics_storage', 'ad_storage', 'ad_user_data', 'ad_personalization'];

// Keep providers independent of the loader and only notify it when consent actually changes.
export const subscribeMzConsent = (source, listener) => {
	let previousSnapshot;
	const emit = (state) => {
		const snapshot = JSON.stringify(state);
		if (snapshot === previousSnapshot) return;
		previousSnapshot = snapshot;
		listener(state);
	};
	return source === 'parent' ? subscribeParentConsent(emit) : subscribeLocalConsent(emit);
};

// The supplied local CMP already translates its vendor/purpose choices into Google Consent Mode.
const subscribeLocalConsent = (emit) => {
	const readConsent = () => {
		let cmpEvent;
		const google = Object.fromEntries(GOOGLE_KEYS.map(key => [key, 'denied']));
		for (const entry of window.dataLayer || []) {
			if (!entry) continue;
			if (entry.event === 'cmpEvent') cmpEvent = entry;
			if (entry[0] === 'consent' && ['default', 'update'].includes(entry[1]) && entry[2]) {
				for (const key of GOOGLE_KEYS) {
					if (['granted', 'denied'].includes(entry[2][key])) google[key] = entry[2][key];
				}
			}
		}
		emit({
			hasExplicitChoice:      cmpEvent?.userChoiceExists === true,
			tagManagerAllowed:      typeof cmpEvent?.cmpConsentVendors === 'string' && cmpEvent.cmpConsentVendors.split(',').includes('s905'),
			google,
			googleConsentPublished: true,
		});
	};
	readConsent();
	const interval = window.setInterval(readConsent, 200);
	return () => window.clearInterval(interval);
};

// Consentmanager extends TCF with its custom vendors; unknown or missing permissions remain denied.
const getParentConsentState = (tcData, hasExplicitChoice) => {
	const customVendors = tcData.customVendorConsents || {};
	const vendors = tcData.vendor?.consents || {};
	const purposes = tcData.purpose?.consents || {};
	const storageAllowed = purposes[1] === true;
	const adsAllowed = vendors[755] === true || customVendors.s1 === true || customVendors.s1498 === true;
	const permission = allowed => hasExplicitChoice && allowed ? 'granted' : 'denied';
	const consentList = (entries) => ',' + Object.keys(entries).filter(key => entries[key] === true).join(',') + ',';
	return {
		hasExplicitChoice,
		tagManagerAllowed: customVendors.s905 === true,
		google:            {
			analytics_storage:  permission(customVendors.s26 === true && storageAllowed),
			ad_storage:         permission(adsAllowed && storageAllowed),
			ad_user_data:       permission(adsAllowed && storageAllowed),
			ad_personalization: permission(adsAllowed && storageAllowed && purposes[3] === true && purposes[4] === true),
		},
		// Existing container triggers need the parent's CMP data inside this iframe's own dataLayer.
		containerEvent: {
			event:                   'cmpEvent',
			userChoiceExists:        hasExplicitChoice,
			cmpConsentString:        tcData.tcString,
			cmpConsentVendors:       consentList({ ...vendors, ...customVendors }),
			cmpConsentPurposes:      consentList({ ...purposes, ...tcData.customPurposeConsents }),
			cmpGoogleVendorsConsent: consentList(tcData.googleVendorConsents || {}),
		},
	};
};

// GTM needs verified choice and live permissions; IMA consumes the same connection's raw readiness.
const subscribeParentConsent = emit => subscribeToParentCmp((state) => {
	if (!state.success) emit(getParentConsentState({}, false));
	else if (state.hasExplicitChoice !== undefined) emit(getParentConsentState(state.tcData, state.hasExplicitChoice));
});
